import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/* the same distance www.hostsharing.net waits for before it offers the
   way back — far enough that the button never appears on a page that
   barely scrolls */
const APPEAR_AFTER = 400;

/*
 * The way back to the top of a long page, as www.hostsharing.net
 * offers it: a button parked in the lower right corner that shows up
 * once the page has been scrolled a screen or so. It is rendered only
 * while it is offered, so it never sits in the tab order as an
 * invisible stop.
 *
 * The corner it parks in is the corner of the window, the footer at
 * the end of the page included. It used to step up as the footer came
 * into view, which put it in the middle of the page just where the
 * page bottom — and whatever else is offered down there — is; the
 * small print it now covers is a scroll away from being read again.
 */
export function BackToTop() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > APPEAR_AFTER);

    /* the page may already be scrolled when this mounts — after a
       reload the browser restores the position */
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  if (!visible) return null;

  const backToTop = () =>
    window.scrollTo({
      top: 0,
      /* whoever asked the system for less motion gets the jump;
         jsdom has no matchMedia, hence the guard */
      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    });

  return (
    <button
      type="button"
      className="btn-back-to-top"
      title={t('common.backToTop')}
      aria-label={t('common.backToTop')}
      onClick={backToTop}
    />
  );
}
